import { Component, computed, inject, input, output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AvatarColors, ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PropertyMemberModel } from '../../../../core/models/data/property-member-model';
import { PropertyMemberTypes } from '../../../../core/enums/data.enums';
import { MemberPermission, PropertyPermission } from '../../../../core/enums/permissions.enums';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { AddMembersToPropertyComponent } from '../add-members-to-property/add-members-to-property.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PropertiesService } from '../../../../core/services/property.service';
import { MembersMessagingConstants } from '../../../../core/constants/messaging.constants';

interface ContactIdentity {
  firstName: string;
  lastName: string;
  memberId: number;
  avatarFileId?: string;
  avatarHexColor?: AvatarColors;
}

@Component({
  selector: 'ms-property-contacts',
  imports: [
    FontAwesomeModule,
    AvatarComponent,
    IfHasPermissionDirective,
    AddMembersToPropertyComponent,
    ConfirmationModalComponent,
    InfoModalComponent
  ],
  templateUrl: './property-profile-contacts.component.html',
  styleUrl: './property-profile-contacts.component.scss'
})
export class PropertyContactsComponent {
  @ViewChild('addMembersRef') addMembersRef?: AddMembersToPropertyComponent;
  @ViewChild('detachConfirmationModalRef') detachConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('detachInfoModalRef') detachInfoModalRef!: InfoModalComponent;

  public property = input<PropertyModel | undefined>(undefined);
  public membersChanged = output<void>();

  public PropertyMemberTypes = PropertyMemberTypes;
  public PropertyPermission = PropertyPermission;

  public isSubmitting = false;
  private targetedMember?: PropertyMemberModel;

  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private bootstrapService = inject(BootstrapService);
  private propertiesService = inject(PropertiesService);
  private router = inject(Router);

  public owners = computed(() =>
    this.property()?.propertyMembers?.filter(m => m.memberType === PropertyMemberTypes.Owner) ?? []
  );

  public tenants = computed(() =>
    this.property()?.propertyMembers?.filter(m => m.memberType === PropertyMemberTypes.Tenant) ?? []
  );

  public hasMembers = computed(() =>
    (this.property()?.propertyMembers?.length ?? 0) > 0
  );

  // The property's own organization, not the viewer's active one — a property profile is reachable
  // for any organization the viewer belongs to and does not change the active one, so these two can
  // diverge (DEV-2643).
  public canViewMembers = computed(() => {
    const authState = this.authorizationStateService.authorizationState();
    const orgId = this.property()?.organizationId;
    if (!authState) return false;
    return this.authorizationService.hasPermission(authState, MemberPermission.Read, orgId);
  });

  public canViewMember(memberId: number): boolean {
    if (this.canViewMembers()) return true;

    const authState = this.authorizationStateService.authorizationState();
    const orgId = this.property()?.organizationId;
    if (!authState || !orgId) return false;

    return authState.memberPermissions?.some(
      mp => mp.organizationId === orgId && mp.memberId === memberId
    ) ?? false;
  }

  public openAddMembersModal(): void {
    this.addMembersRef?.openAddMembersToPropertyModal();
  }

  public onMembersAddedToProperty(): void {
    this.membersChanged.emit();
  }

  public toIdentity(member: PropertyMemberModel): ContactIdentity {
    return {
      firstName: member.firstName,
      lastName: member.lastName,
      memberId: member.memberId,
      avatarFileId: member.avatarFileId,
      avatarHexColor: member.avatarHexColor as AvatarColors
    };
  }

  public viewMember(member: PropertyMemberModel): void {
    if (!this.canViewMember(member.memberId)) return;
    this.router.navigate(['/members/profile/', member.memberId]);
  }

  public requestDetach(member: PropertyMemberModel): void {
    this.targetedMember = member;

    this.detachConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.detachConfirmationModalRef.confirmButtonText.set('Detach');
    this.detachConfirmationModalRef.message.set(
      MembersMessagingConstants.MEMBERS_DETACH_FROM_PROPERTY_CONFIRMATION().replaceValuesWithObject({
        firstName: member.firstName,
        lastName: member.lastName
      })
    );

    this.bootstrapService.manageModal('#detachMemberFromPropertyConfirmationModal', ModalActions.Show);
  }

  public async confirmDetach(): Promise<void> {
    const propertyId = this.property()?.propertyId;
    if (!this.targetedMember || !propertyId) return;

    this.isSubmitting = true;
    try {
      const result = await this.propertiesService.removeMemberFromProperty(
        propertyId,
        { memberIds: [this.targetedMember.memberId] }
      );

      this.detachInfoModalRef.modalType.set(result.success ? ModalTypes.Success : ModalTypes.Failure);
      this.detachInfoModalRef.message.set(result.message);

      this.bootstrapService.manageModal('#detachMemberFromPropertyConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#detachMemberFromPropertyInfoModal', ModalActions.Show);
    } finally {
      this.isSubmitting = false;
    }
  }

  public cancelDetach(): void {
    this.bootstrapService.manageModal('#detachMemberFromPropertyConfirmationModal', ModalActions.Hide);
  }

  public onDetachInfoConfirmed(): void {
    this.targetedMember = undefined;
    this.membersChanged.emit();
  }
}
